<template>
    <Price
        :minimumPerNightPrice="minimumPerNightPrice"
        :minimumPerPersonPrice="minimumPerPersonPrice"
        :minimumPrice="minimumPrice"
        :showPrefix="showPrefix"
        :hide-cents="isAccessCodeProduct"
        :hide-help="isValueVoucher"
        :prefix="prefix"
        small-prefix
        :ignore-price-mode="ignorePriceMode"
        class="product-price"
        :class="{ 'product-price--darker-help': isAccessCodeRequired, 'product-price--large': isAccessCodeProduct }"
    >
        <DiscountBadge v-if="isDiscountVisible" :discount="discountValue" class="product-price__discount" />
    </Price>
</template>

<script>
import Price from '@/components/Price.vue';
import DiscountBadge from '@/components/DiscountBadge.vue';

export default {
    name: 'ProductSelectHeaderPrice',

    components: { DiscountBadge, Price },

    props: {
        productDetails: {
            required: true,
            type: Object
        },
        isValueVoucher: Boolean,
        isOtherProduct: Boolean
    },

    computed: {
        isAccessCodeRequired() {
            return this.$store.state.globalMeta.accessCodeRequired;
        },
        priceModeProperty() {
            return this.$store.state.priceMode.property;
        },
        price() {
            return this.$getSafe(this.productDetails, 'price.regularPriceValues') || {};
        },
        minimumPerNightPrice() {
            if (this.isValueVoucher || this.isAccessCodeProduct || this.isPartnerShopWithPackagePricing) {
                return undefined;
            }

            return this.isMainShop ||
                this.isPartnershopWithPerNightPricing ||
                (this.isAccessCodeRequired && !this.isOtherProduct)
                ? this.$getSafe(this.price, 'minimumPerPersonPerNightPrice.perPersonPerNightPrice')
                : undefined;
        },
        minimumPerPersonPrice() {
            if (this.isValueVoucher || this.isAccessCodeProduct || this.isPartnerShopWithPackagePricing) {
                return undefined;
            }
            return !this.isAccessCodeRequired && !this.isOtherProduct
                ? this.$getSafe(this.price, 'minimumPerPersonPrice.perPersonPrice')
                : undefined;
        },
        minimumPrice() {
            if (
                this.isOtherProduct ||
                this.isValueVoucher ||
                this.isAccessCodeProduct ||
                this.isPartnerShopWithPackagePricing
            ) {
                return this.$getSafe(this.price, 'minimumPrice.price');
            }
            // minimum price for regular hotel vouchers should be ignored
            return undefined;
        },
        showPrefix() {
            return (
                !this.isAccessCodeProduct &&
                (this.productDetails.variants.length > 1 || this.productDetails.scheduledArrivalAvailable)
            );
        },
        isAccessCodeProduct() {
            return this.productDetails.webshopAccessCodeProduct;
        },
        prefix() {
            if (!this.isValueVoucher) {
                return undefined;
            }
            return this.productDetails.discountAvailable ? this.$t('Price.prefixDiscount') : this.$t('Price.prefixFor');
        },
        discountValue() {
            if (this.isValueVoucher) {
                return this.productDetails.discountValue;
            }
            return this.price[this.priceModeProperty].discountPercentage;
        },
        isDiscountVisible() {
            if (this.isAccessCodeProduct) {
                return false;
            }

            if (this.isValueVoucher && this.productDetails.discountVisible) {
                return this.productDetails.discountPercentage;
            }

            return (
                this.$store.state.globalMeta.discountVisible &&
                this.price[this.priceModeProperty] &&
                this.price[this.priceModeProperty].discountPercentage
            );
        },
        isMainShop() {
            return this.$store.getters.isMainShop;
        },
        isPartnershopWithPerNightPricing() {
            return this.$store.getters.isPartnershopWithPerNightPricing;
        },
        isPartnerShopWithPackagePricing() {
            return this.$store.getters.isPartnerShopWithPackagePricing;
        },
        ignorePriceMode() {
            return (
                (!this.isMainShop && !this.isPartnershopWithPerNightPricing) ||
                this.minimumPerNightPrice === undefined ||
                this.minimumPerPersonPrice === undefined
            );
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/theming' as *;
@use '@/styles/mixins';

.product-price {
    color: inherit;
    font-size: 30px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;

    &.price ::v-deep {
        .help-text {
            position: relative;
            bottom: 0;
            letter-spacing: normal;
            width: 100%;
            text-align: center;
            font-size: 0.6em;
        }
    }

    &__discount {
        font-size: 20px;
        margin-left: 5px;
    }

    &--large {
        font-size: 70px;
    }

    &--darker-help {
        &.price ::v-deep .help-text {
            &.darker-help ::v-deep .help-text {
                color: var(--product-select-price-color-darker);
            }
        }
    }

    @include mixins.mobile-only {
        &--large {
            font-size: 30px;
        }
    }
}
</style>
